import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

import { 
  Breadcrumbs, Box, Button,
  Collapse, Checkbox,
  FormControl,
  IconButton, InputAdornment, InputLabel, 
  MenuItem, Modal,
  OutlinedInput,
  Select, 
  TextField, Typography, 
  useTheme 
} from "@mui/material";

import styled from "styled-components";

import SyncIcon from "@mui/icons-material/Sync";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_KR } from "@ag-grid-community/locale";
//import 'ag-grid-community/styles/ag-grid.css'; // 필수 스타일 (ag grid v32 버전)
//import 'ag-grid-community/styles/ag-theme-alpine.css'; // 테마 스타일 (ag grid v32 버전)
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Theming API 전용 스타일(ag grid v33 버전)

import { 
  useGetTableData, 
  useCreateTableData, 
  usePutTableData,
  useDeleteTableData, 
} from "../../../components/Table/hooks";

import { standardEmployeeColumns } from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { 
  BreadcrumbsTable,
  ConfirmDialog,
  SnackbarAlert,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { COLORS } from "../../../styles/colors";
import { FONT_PROPS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

///////////////////////////////
// 정보관리의 직원 정보 컴포넌트
///////////////////////////////
const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "직원 정보";
export function StandardEmployee() {
  const theme = useTheme();
  const { group_code, project_code } = useParams();

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 마스터 데이터의 직원 조회 요청
  const getMasterEmployeeData = useGetTableData(
    "request",
    "/getWorkerList",
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableData(
    "request",
    "/getStandardEmployee",
    group_code,
    project_code
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postStandardEmployee",
    group_code,
    project_code
  );
  // 데이터 수정 요청 
  // const putData = usePutTableData( 
  //   "request",
  //   "/putStandardEmployee",
  //   group_code,
  //   project_code
  // );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteStandardEmployee",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>
        {/* 동기화 버튼 */}
        <Buttons.Custom
          startIcon={<SyncIcon />}
          onClick={() => {
            openSyncModal(); // 동기화 모달 열기
          }}
          sx={{ padding: '12px 16px' }}
        >
          동기화
        </Buttons.Custom>
        {/* 삭제 버튼 */}
        <Buttons.Delete 
          onClick={openDeleteModal} // 행 삭제 로직 실행
          disabled={table.getSelectedRowModel().rows.length === 0}
          sx={{ padding: '12px 16px' }}
        />    
      </Box>
  </Box>
  );

  // 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });
  // DB 업데이트 (삭제 데이터)
  const updateDeleteData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.emp_id);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'emp_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
      
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  ////////////////////////////
  // 장비 동기화 관련 
  ////////////////////////////

  // 동기화 모달 상태 관리
  const [syncModal, setSyncModal]  = useState({ isOpen: false }); 
  const openSyncModal = () => setSyncModal({ isOpen: true});
  const closeSyncModal = () => setSyncModal({ isOpen: false });
  // DB 업데이트 (추가 데이터)
  const updateAddData = async (addData) => {
    try{
      //console.log("새로운 데이터: ", addData);

      const result = await postData.mutateAsync(addData);
      if(result.status === 200) {
        openDataAlert(message.updateDataSyncSuccess);
      }

      //openSyncModal();
    }
    catch(error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {/** 직원 정보 테이블 표시 */}
        <NewTableTemplate 
          ref={tableRef}
          columns={standardEmployeeColumns}
          data={getData.data || []}
          topToolbarContent={customTableTopToolbar}
          //renderRowActionContent={customRenderRowActionContent}
          enableCustomGlobalFilter={true}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
        {/* 직원 정보 동기화 모달 */}
        <SyncEmployeeListModal 
          isOpen={syncModal.isOpen}
          onConfirm={updateAddData}
          onCancel={closeSyncModal}
          title="직원 정보 동기화"
          masterEmployeeData={getMasterEmployeeData.data || []}
          standardEmployeeData={getData.data || []}
        />
        {/* 행(row) 수정 모달 */}
        {/* <EditRowModal 
          isOpen={showEditModal.isOpen}
          originalRowData={showEditModal.rowData}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        /> */}
        {/* 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={updateDeleteData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        /> 
      </TableContainer>
    );
  }

  // StandardEmployee 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
};

/**
 * 직원 동기화 관련 객체 정의 및 함수
 */
// AG Grid Colums 정의
const employeeListColumn = [
  {
    field: 'emp_id', // 칼럼 데이터 key
    headerName: '직원 칼럼 ID', // 칼럼 헤더 이름
    flex: 1,
    cellStyle: { textAlign: 'center' },     // 셀 내용 가로 가운데
    headerClass: 'header-center',           // 헤더 가운데 (CSS 필요)
    //headerClass: innerStyles.headerText, // 특정 칼럼에 css 적용 시 hearclass,cellclass 사용
    //cellClass: innerStyles.cellText,
    filter: true, // 필터 기능 활성화 
    valueFormatter: (params) => { // cell 데이터 변경
      const { emp_id, worker_id } = params.data;
      return emp_id || worker_id;
    },
  },
  { 
    field: 'emp_name',
    headerName: '직원명',
    flex: 1,
    cellStyle: { textAlign: 'center' },     // 셀 내용 가로 가운데
    headerClass: 'header-center',           // 헤더 가운데 (CSS 필요)
    //headerClass: innerStyles.headerText,
    //cellClass: innerStyles.cellText,
    filter: true,
    valueFormatter: (params) => { // cell 데이터 변경
      const { emp_name, worker_name } = params.data;
      return emp_name || worker_name;
    },
  }, 
]
// 직원 정보 동기화 모달 컴포넌트
const SyncEmployeeListModal = ({
  isOpen,
  onConfirm = null,
  onCancel = null,
  masterEmployeeData = null, // 마스터 데이터
  standardEmployeeData = null, // 설비기준정보 데이터
}) => {
  const theme = useTheme();

  // 선택한 행의 정보
  const [selectedRow, setSelectedRow] = useState([]);

  // ag grid 정보
  const gridRef = useRef();
  const clearSelection = () => {
    // ag grid의 선택한 행 초기화
    gridRef.current.api.deselectAll();
  };

  // 장비 목록 동기화 테이블의 선택 유/무 판단
  const handleSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    if(selected.length > 0) {
      setSelectedRow(selected);
      //console.log('선택된 행:', selected);
    }
    else {
      setSelectedRow([]);
    }
  };

  // 직원 동기화 처리
  const handleSyncEmployee = async () => {
    //console.log(selectedRow);

    // 데이터 포맷 변경
    const datas = selectedRow.map(item => {
      return item.worker_id;
    });

    onConfirm(datas);
    clearSelection();
    setSelectedRow([]);
  }

  // 렌더링
  return (
    <Modal 
      open={isOpen}
      onClose={onCancel}
    >
      {/* 컨테이너 */}
      <Box 
        sx={{ 
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "8px",
          boxShadow: 24,
          padding: theme.spacing(4),

          width: 'auto',
          minWidth: '25%',
          maxWidth: '90%',
          maxHeight: '90vh',  // ✅ 전체 제한
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 타이틀 */}
        <Box 
          sx={{
            fontSize: "18px",                
            fontWeight: FONT_PROPS.MEDIUM,
            color: FONT_PROPS.DARK,
            padding: "0px 0px 20px 0px",
            borderBottom: `1px solid ${COLORS.BORDER_BRIGHT}`,
          }}
        >
          {CHILD_TITLE} 동기화
        </Box>
        {/** 직원 정보 */}
        <Box display='flex' gap={5} padding='12px'>
          <Box display='flex' flexDirection='column'>
            <Typography fontSize={16} fontWeight={FONT_PROPS.MEDIUM} mb={2} mt={2} ml={4}>
              {CHILD_TITLE}
            </Typography>
            <div className="ag-theme-quartz-machine" style={{ height: 500, width: '100%', minWidth: 550 }}>
              <AgGridReact
                rowData={standardEmployeeData} // 데이터
                columnDefs={employeeListColumn} // 데이터 칼럼 정의
                pagination={true} // 페이지 네이션 사용 유무
                //paginationPageSize={100} // 페이지 네이션 선택 사이즈
                //paginationPageSizeSelector={[5, 10, 20, 50, 100]} // 페이지 네이션 사이즈 설정
                //paginationPageSizeSelector={false} // ✅ 드롭다운 비활성화
                localeText={AG_GRID_LOCALE_KR} // 👈 한글 적용
              />
            </div>
          </Box>
          <Box display='flex' flexDirection='column' >
            <Typography fontSize={16} fontWeight={FONT_PROPS.MEDIUM} mb={2} mt={2} ml={4}>
              직원 정보(마스터데이터)
            </Typography>
            <div className="ag-theme-quartz-machine" style={{ height: 500, width: '100%', minWidth: 550 }}>
              <AgGridReact
                ref={gridRef}
                rowData={masterEmployeeData} // 데이터
                columnDefs={employeeListColumn} // 데이터 칼럼 정의
                pagination={true} // 페이지 네이션 사용 유무
                //paginationPageSize={100} // 페이지 네이션 선택 사이즈
                //paginationPageSizeSelector={[5, 10, 20, 50, 100]} // 페이지 네이션 사이즈 설정
                //paginationPageSizeSelector={false} // ✅ 드롭다운 비활성화
                localeText={AG_GRID_LOCALE_KR} // 👈 한글 적용
                rowSelection={{
                  mode: 'multiRow', // ✅ v33에서 'multiple' 대신 사용
                  //allowMultipleRanges: true, // 선택적으로 사용 가능
                }}
                onSelectionChanged={handleSelectionChanged}
              />
            </div>
          </Box>
        </Box>
        {/** 버튼 */}
                <Box 
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${COLORS.BORDER_BRIGHT}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        > 
          <Button 
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleSyncEmployee}
            disabled = {selectedRow.length === 0}
          >
            가져오기
          </Button>
          <Button 
            variant="outlined" 
            onClick={onCancel}
          >
            닫기
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};